import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import Group_tic from "../../Images/Group_tic.svg";
import { API_URL } from "../../config/api";

const HIDE_CREATE_ROLES = ["super_admin"];

const MyProgram = ({ userData }) => {
  const navigate = useNavigate();
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [roleLoaded, setRoleLoaded] = useState(false); // ✅ added

  const outletContext = useOutletContext?.() || {};
  const resolvedUserData = userData || outletContext?.userData;
  const dashboard = resolvedUserData?.dashboard || {};

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setRoleLoaded(true); // ✅ no token, mark loaded
          return;
        }
        const res = await fetch(`${API_URL}/current_user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCurrentUserRole(data?.roles?.[0]?.name || null);
      } catch (err) {
        console.error("Failed to fetch role:", err);
      } finally {
        setRoleLoaded(true); // ✅ always mark loaded
      }
    };
    fetchRole();
  }, []);

  // ✅ Wait for role to load before showing button — no flash
  const showCreateButton = roleLoaded && !HIDE_CREATE_ROLES.includes(currentUserRole);

  const scheduledPrograms = dashboard?.scheduled_programs_list || [];
  const nonScheduledPrograms = dashboard?.non_scheduled_programs_list || [];

  const allPrograms = [
    ...scheduledPrograms.map((p) => ({ ...p, isScheduled: true })),
    ...nonScheduledPrograms.map((p) => ({ ...p, isScheduled: false })),
  ];

  const handleClick = () => navigate("/ins_program/create_program");

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isDiscountValid = (schedule) => {
    if (!schedule) return false;

    const discountApplicable =
      schedule.discount_applicable === true ||
      schedule.discount_applicable === "true";

    if (!discountApplicable) return false;

    const discountValue = Number(schedule.discount_value);
    if (!discountValue || discountValue <= 0) return false;

    const now = new Date();

    const validFrom = schedule.discount_valid_from
      ? new Date(schedule.discount_valid_from)
      : null;

    const validTo = schedule.discount_valid_to
      ? new Date(schedule.discount_valid_to)
      : null;

    if (validFrom && now < validFrom) return false;
    if (validTo && now > validTo) return false;

    return true;
  };

  const calculateDiscountedPrice = (schedule, originalPrice) => {
    if (!isDiscountValid(schedule)) return originalPrice;

    const discountType = schedule.discount_type;
    const discountValue = Number(schedule.discount_value);
    const price = Number(originalPrice);

    if (!price || price <= 0) return 0;

    if (discountType === "Percentage")
      return Math.max(0, price - (price * discountValue) / 100);

    if (discountType === "FlatAmount")
      return Math.max(0, price - discountValue);

    return originalPrice;
  };

  const programCards = allPrograms.map((program) => {
    const course = program?.isScheduled ? program?.course || {} : program || {};
    const currentSchedule = program?.isScheduled ? program : null;

    const startDateRaw = currentSchedule?.start_date || course?.start_date;
    const endDateRaw = currentSchedule?.end_date || course?.end_date;

    const startDateObj = startDateRaw ? new Date(startDateRaw) : null;
    const endDateObj = endDateRaw ? new Date(endDateRaw) : null;

    if (startDateObj) startDateObj.setHours(0, 0, 0, 0);
    if (endDateObj) endDateObj.setHours(0, 0, 0, 0);

    const startDate = startDateObj
      ? startDateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "N/A";

    let badge = "";
    let badgeColor = "";

    if (program?.isScheduled) {
      if (startDateObj && startDateObj > today) {
        badge = "Upcoming";
        badgeColor = "bg-blue-100 text-blue-800";
      } else if (endDateObj && today > endDateObj) {
        badge = "Completed";
        badgeColor = "bg-gray-200 text-gray-700";
      } else if (
        startDateObj &&
        endDateObj &&
        startDateObj <= today &&
        today <= endDateObj
      ) {
        badge = "Ongoing";
        badgeColor = "bg-emerald-100 text-lime-800";
      }
    }

    const originalPrice = Number(course?.amount || 0);

    const discountedPrice = currentSchedule
      ? calculateDiscountedPrice(currentSchedule, originalPrice)
      : originalPrice;

    const hasDiscount =
      currentSchedule &&
      isDiscountValid(currentSchedule) &&
      discountedPrice < originalPrice;

    const instructors = program?.instructors || [];

    return {
      course_id: course?.course_id || 0,
      title: course?.title || "Untitled",
      location: program?.isScheduled
        ? currentSchedule?.location || "N/A"
        : course?.location || "N/A",
      image:
        course?.image ||
        course?.program_thumbnail ||
        `https://placehold.co/270x201?text=${encodeURIComponent(
          course?.title || "Course"
        )}`,
      startDate,
      priceOld: originalPrice,
      priceNew: discountedPrice,
      hasDiscount,
      badge,
      badgeColor,
      modules: `${course?.total_modules || 0} Modules`,
      duration: currentSchedule?.total_duration_days
        ? `${currentSchedule.total_duration_days} Days`
        : course?.duration || "N/A",
      isScheduled: program?.isScheduled || false,
      schedule_id: currentSchedule?.schedule_id || null,
      is_published: course?.is_published ?? false,
      instructors,
    };
  });

  const statsCards = [
    {
      title: "Created Programs",
      value:
        (dashboard?.scheduled_programs || 0) +
        (dashboard?.non_scheduled_programs || 0),
    },
    { title: "Scheduled Programs", value: dashboard?.scheduled_programs || 0 },
    { title: "Ongoing Programs", value: dashboard?.ongoing_programs || 0 },
    { title: "Completed Programs", value: dashboard?.completed_programs || 0 },
  ];

  return (
    <div className="w-full flex flex-col gap-6 p-6 bg-gray-50">

      <div className="flex items-center justify-between">
        <h2 className="text-zinc-800 text-lg font-bold">My Programs</h2>

        {/* ✅ Only renders after role is confirmed — no flash */}
        {showCreateButton && (
          <button
            onClick={handleClick}
            className="flex items-center gap-2 px-4 py-2 bg-[#4AACC5] hover:bg-[#75cee4] text-white text-sm font-semibold rounded-lg shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Create Program
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-6">
        {statsCards.map((card, i) => (
          <div key={i} className="w-full sm:w-56 h-28 bg-white rounded shadow flex items-center px-4 gap-3">
            <div className="w-16 h-16 bg-cyan-100 rounded flex items-center justify-center">
              <img src={Group_tic} alt="" className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium">{card.title}</p>
              <p className="text-black text-2xl font-bold">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        {programCards.length === 0 && (
          <div className="col-span-4 flex items-center justify-center py-16 text-gray-400">
            <p className="text-base font-medium">No programs found</p>
          </div>
        )}

        {programCards.map((program, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-lg overflow-hidden relative cursor-pointer hover:shadow-xl transition"
          >
            <img src={program.image} alt={program.title} className="w-full h-44 object-cover" />

            <div className="p-4 relative">

              <div className="absolute left-4 top-4 text-xs rounded-3xl px-3 py-1 bg-blue-100 text-blue-800 truncate max-w-[45%]">
                {program.location}
              </div>

              {program.isScheduled && program.badge ? (
                <div className={`absolute right-4 top-4 text-xs rounded-3xl px-3 py-1 ${program.badgeColor}`}>
                  {program.badge}
                </div>
              ) : !program.isScheduled ? (
                <div className="absolute right-4 top-4 text-xs rounded-3xl px-3 py-1 bg-yellow-100 text-yellow-800">
                  Unscheduled
                </div>
              ) : null}

              <div className="text-gray-900 font-medium text-base mb-2 mt-8 line-clamp-2">
                {program.title}
              </div>

              <div className="flex gap-2 mb-2 text-sm">
                {program.hasDiscount ? (
                  <>
                    <span className="line-through text-red-500">${program.priceOld.toFixed(2)}</span>
                    <span className="text-green-600 font-semibold">${program.priceNew.toFixed(2)}</span>
                  </>
                ) : (
                  <span className="text-gray-900 font-semibold">${program.priceOld.toFixed(2)}</span>
                )}
              </div>

              <div className="border-t border-zinc-200 pt-2">
                <div className="flex items-center gap-2 text-neutral-700 text-xs font-normal">
                  <span>{program.modules}</span>
                  <div className="w-px h-2 bg-stone-300 rotate-[25deg]" />
                  <span>{program.duration}</span>
                  <div className="w-px h-2 bg-stone-300 rotate-[25deg]" />
                  <span className="whitespace-nowrap truncate">{program.startDate}</span>
                </div>
              </div>

            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyProgram;